import BaseFormController from "../../../validation/BaseFormController";
import {FormField, OptionalFormField, RequiredFormField} from "../../../validation/FormField";
import {none, Option, option} from "ts-option";
import FormValidation from "../../../validation/FormValidation";
import {ValidationFailure, ValidationResult} from "../../../validation/ValidationResult";
import Brand from "../../../model/brand/Brand";
import {
	DealerPriceMatrixState,
	createEmptyMatrixState,
	fromCreditAmounts,
	fromDealerPrices,
	hasAtLeastOneFilledCell,
	isMatrixValid,
	toCreditAmounts,
	toDealerPrices
} from "./DealerPriceMatrixState";
import FormValidators from "../../../validation/FormValidators";
import Address from "../../../model/address/Address";
import NumberUtils from "../../../utils/NumberUtils";

const uuidv4 = require("uuid/v4");

/**
 * Brand form data.
 */
export default class BrandForm extends BaseFormController<BrandForm> {
	constructor(formId: string,
		public readonly brandName: RequiredFormField<string>,
		public readonly labourCost: RequiredFormField<string>,
		public readonly domain: OptionalFormField<string>,
		public readonly phoneNumber: RequiredFormField<string>,
		public readonly logoId: OptionalFormField<string>,
		public readonly address: OptionalFormField<Address>,
		public readonly vatRegistrationNumber: OptionalFormField<string>,
		public readonly freeText: OptionalFormField<string>,
		public readonly gasNumber: OptionalFormField<string>,
		public readonly email: OptionalFormField<string>,
		public readonly vatRegistered: RequiredFormField<boolean>,
		public readonly automaticPaymentLimit: RequiredFormField<string>,
		public readonly customPartsMarkup: RequiredFormField<number>,
		public readonly rewardPointValue: RequiredFormField<string>,
		public readonly vatRate: RequiredFormField<number>,
		public readonly maxFee: RequiredFormField<string>,
		public readonly creditAmountMatrix: DealerPriceMatrixState,
		public readonly creditAmountAirToWaterHp: RequiredFormField<string>,
		public readonly creditAmountAirToAirHp: RequiredFormField<string>,
		public readonly creditAmountWaterToWaterHp: RequiredFormField<string>,
		public readonly creditAmountBrineToWaterHp: RequiredFormField<string>,
		public readonly creditAmountFlowsave: RequiredFormField<string>,
		public readonly creditValueAirToWaterHp: RequiredFormField<string>,
		public readonly creditValueAirToAirHp: RequiredFormField<string>,
		public readonly creditValueWaterToWaterHp: RequiredFormField<string>,
		public readonly creditValueBrineToWaterHp: RequiredFormField<string>,
		public readonly creditValueFlowsave: RequiredFormField<string>,
		public readonly creditValueCap: RequiredFormField<string>,
		public readonly dealerPriceMatrix: DealerPriceMatrixState,
		public readonly validated: boolean = false) {
		super(brandName, formId);
	}


	static createBrandForm({
		brandName = none,
		labourCost = none,
		domain = none,
		phoneNumber = none,
		logoId = none,
		address = none,
		vatRegistrationNumber = none,
		freeText = none,
		gasNumber = none,
		email = none,
		vatRegistered = none,
		automaticPaymentLimit = none,
		customPartsMarkup = none,
		rewardPointValue = none,
		vatRate = none,
		maxFee = none,
		creditAmountMatrix = createEmptyMatrixState(),
		creditAmountAirToWaterHp = none,
		creditAmountAirToAirHp = none,
		creditAmountWaterToWaterHp = none,
		creditAmountBrineToWaterHp = none,
		creditAmountFlowsave = none,
		creditValueAirToWaterHp = none,
		creditValueAirToAirHp = none,
		creditValueWaterToWaterHp = none,
		creditValueBrineToWaterHp = none,
		creditValueFlowsave = none,
		creditValueCap = none,
		dealerPriceMatrix = createEmptyMatrixState()
	}) {
		return new BrandForm(
			uuidv4(),
			RequiredFormField.create<string>(brandName, "Brand name is required", [
				FormValidators.nonEmptyString("Brand name is required")
			]),
			RequiredFormField.create<string>(labourCost, "Labour cost is required",
				[FormValidators.notLessThanValue("Labour cost should be at least €1", 1)]),
			OptionalFormField.create<string>(domain, [FormValidators.optionalWebsite("Wrong format. Fe. https://hiber.com")]),
			RequiredFormField.create<string>(phoneNumber, "Phone number is required",
				[FormValidators.isPhoneNumberFormat("Invalid phone number")]),
			OptionalFormField.create<string>(logoId),
			OptionalFormField.create<Address>(address),
			OptionalFormField.create<string>(vatRegistrationNumber),
			OptionalFormField.create<string>(freeText),
			OptionalFormField.create<string>(gasNumber),
			OptionalFormField.create<string>(email, [FormValidators.optionalEmail]),
			RequiredFormField.create<boolean>(vatRegistered, "VAT registered status is required"),
			RequiredFormField.create<string>(automaticPaymentLimit, "Automatic payment limit is required"),
			RequiredFormField.create<number>(customPartsMarkup, "Custom parts markup is required"),
			RequiredFormField.create<string>(rewardPointValue, "Reward point value is required"),
			RequiredFormField.create<number>(vatRate, "VAT rate is required"),
			RequiredFormField.create<string>(maxFee, "Max fee is required"),
			creditAmountMatrix,
			RequiredFormField.create<string>(creditAmountAirToWaterHp, "Energy credits - Air to Water is required"),
			RequiredFormField.create<string>(creditAmountAirToAirHp, "Energy credits - Air to Air is required"),
			RequiredFormField.create<string>(creditAmountWaterToWaterHp, "Energy credits - Water to Water is required"),
			RequiredFormField.create<string>(creditAmountBrineToWaterHp, "Energy credits - Brine to Water is required"),
			RequiredFormField.create<string>(creditAmountFlowsave, "Energy credits - Flowsave is required"),
			RequiredFormField.create<string>(creditValueAirToWaterHp, "Payout amount - Air to Water is required"),
			RequiredFormField.create<string>(creditValueAirToAirHp, "Payout amount - Air to Air is required"),
			RequiredFormField.create<string>(creditValueWaterToWaterHp, "Payout amount - Water to Water is required"),
			RequiredFormField.create<string>(creditValueBrineToWaterHp, "Payout amount - Brine to Water is required"),
			RequiredFormField.create<string>(creditValueFlowsave, "Payout amount - Flowsave is required"),
			RequiredFormField.create<string>(creditValueCap, "Credit value cap is required",
				[FormValidators.notLessThanValue("Credit value cap cannot be negative", 0)]),
			dealerPriceMatrix
		)
	}

	public static createEmpty(): BrandForm {
		return this.createBrandForm({
			vatRegistered: option(false),
			automaticPaymentLimit: option("1000.00"),
			customPartsMarkup: option(0),
			// What every brand was backfilled to, so a brand created here starts out matching the rest.
			creditValueCap: option("80.00")
		});
	}

	static createFromBrand(brand: Brand, address: Option<Address>) {
		return BrandForm.createBrandForm({
			brandName: option(brand.name),
			labourCost: option((brand.labourCost / 100).toFixed(2)),
			domain: brand.domain,
			phoneNumber: option(brand.phoneNumber),
			logoId: brand.logoId,
			address: address,
			vatRegistrationNumber: brand.vatRegistrationNumber,
			freeText: brand.freeText,
			gasNumber: brand.gasNumber,
			email: brand.email,
			vatRegistered: option(brand.vatRegistered),
			automaticPaymentLimit: option((brand.automaticPaymentLimit / 100).toFixed(2)),
			customPartsMarkup: option(brand.customPartsMarkup),
			rewardPointValue: option((brand.rewardPointValue / 100).toFixed(2)),
			vatRate: option(brand.vatRate),
			maxFee: option((brand.maxFee / 100).toFixed(2)),
			creditAmountMatrix: fromCreditAmounts(brand.creditAmounts),
			dealerPriceMatrix: fromDealerPrices(brand.dealerPrices),
			creditAmountAirToWaterHp: brand.creditAmountAirToWaterHp.map(v => v.toString()),
			creditAmountAirToAirHp: brand.creditAmountAirToAirHp.map(v => v.toString()),
			creditAmountWaterToWaterHp: brand.creditAmountWaterToWaterHp.map(v => v.toString()),
			creditAmountBrineToWaterHp: brand.creditAmountBrineToWaterHp.map(v => v.toString()),
			creditAmountFlowsave: brand.creditAmountFlowsave.map(v => v.toString()),
			creditValueAirToWaterHp: brand.creditValueAirToWaterHp.map(v => (v / 100).toFixed(2)),
			creditValueAirToAirHp: brand.creditValueAirToAirHp.map(v => (v / 100).toFixed(2)),
			creditValueWaterToWaterHp: brand.creditValueWaterToWaterHp.map(v => (v / 100).toFixed(2)),
			creditValueBrineToWaterHp: brand.creditValueBrineToWaterHp.map(v => (v / 100).toFixed(2)),
			creditValueFlowsave: brand.creditValueFlowsave.map(v => (v / 100).toFixed(2)),
			// Wrapped rather than divided straight away: a brand served by a backend that predates the
			// column has no amount, and "NaN" in a currency field is worse than an empty one the admin is
			// then asked to fill in.
			creditValueCap: option(brand.creditValueCap).map(v => (v / 100).toFixed(2))
		});
	}

	validate(): BrandForm {
		const validated = FormValidation.validateForm<BrandForm>(this, (form, config) => form.copy(config));
		return validated.copy({validated: true});
	}

	getDealerPriceMatrixError(): string | undefined {
		if (!this.validated) { return undefined; }
		if (!hasAtLeastOneFilledCell(this.dealerPriceMatrix)) {
			return "At least one dealer price is required";
		}
		if (!isMatrixValid(this.dealerPriceMatrix)) {
			return "Some dealer prices are invalid";
		}
		return undefined;
	}

	getCreditAmountMatrixError(): string | undefined {
		if (!this.validated) { return undefined; }
		if (!hasAtLeastOneFilledCell(this.creditAmountMatrix)) {
			return "At least one credit amount is required";
		}
		if (!isMatrixValid(this.creditAmountMatrix, 9999999)) {
			return "Some credit amounts are invalid";
		}
		return undefined;
	}

	onValid(onValid: (brand: Brand) => void, onInvalid: () => void): ValidationResult<Brand> {
		if (!isMatrixValid(this.dealerPriceMatrix) || !isMatrixValid(this.creditAmountMatrix, 9999999)) {
			return new ValidationFailure<Brand>([]).onFailure(() => onInvalid());
		}
		return FormValidation.validateFields(
			[this.brandName, this.labourCost, this.phoneNumber, this.email, this.domain, this.vatRegistered,
				this.automaticPaymentLimit, this.customPartsMarkup, this.rewardPointValue, this.vatRate, this.maxFee,
				this.creditAmountAirToWaterHp, this.creditAmountAirToAirHp, this.creditAmountWaterToWaterHp,
				this.creditAmountBrineToWaterHp, this.creditAmountFlowsave, this.creditValueAirToWaterHp,
				this.creditValueAirToAirHp, this.creditValueWaterToWaterHp, this.creditValueBrineToWaterHp,
				this.creditValueFlowsave, this.creditValueCap],
			() => {
				return new Brand(
					uuidv4,
					this.brandName.getValue().trim(),
					NumberUtils.multiplyAndRound(Number(this.labourCost.getValue()), 100),
					this.domain.getOptionalValue(),
					this.phoneNumber.getValue(),
					this.logoId.getOptionalValue(),
					none,
					this.vatRegistrationNumber.getOptionalValue(),
					this.freeText.getOptionalValue(),
					this.gasNumber.getOptionalValue(),
					this.email.getOptionalValue(),
					this.vatRegistered.getValue(),
					NumberUtils.multiplyAndRound(Number(this.automaticPaymentLimit.getValue()), 100),
					this.customPartsMarkup.getValue(),
					NumberUtils.multiplyAndRound(Number(this.rewardPointValue.getValue()), 100),
					this.vatRate.getValue(),
					NumberUtils.multiplyAndRound(Number(this.maxFee.getValue()), 100),
					toCreditAmounts(this.creditAmountMatrix),
					toDealerPrices(this.dealerPriceMatrix),
					option(Number(this.creditAmountAirToWaterHp.getValue())),
					option(Number(this.creditAmountAirToAirHp.getValue())),
					option(Number(this.creditAmountWaterToWaterHp.getValue())),
					option(Number(this.creditAmountBrineToWaterHp.getValue())),
					option(Number(this.creditAmountFlowsave.getValue())),
					option(NumberUtils.multiplyAndRound(Number(this.creditValueAirToWaterHp.getValue()), 100)),
					option(NumberUtils.multiplyAndRound(Number(this.creditValueAirToAirHp.getValue()), 100)),
					option(NumberUtils.multiplyAndRound(Number(this.creditValueWaterToWaterHp.getValue()), 100)),
					option(NumberUtils.multiplyAndRound(Number(this.creditValueBrineToWaterHp.getValue()), 100)),
					option(NumberUtils.multiplyAndRound(Number(this.creditValueFlowsave.getValue()), 100)),
					NumberUtils.multiplyAndRound(Number(this.creditValueCap.getValue()), 100)
				);
			}).onSuccess(brand => onValid(brand)).onFailure(() => onInvalid());
	}

	copy({
		brandName = this.brandName,
		labourCost = this.labourCost,
		domain = this.domain,
		phoneNumber = this.phoneNumber,
		logoId = this.logoId,
		address = this.address,
		vatRegistrationNumber = this.vatRegistrationNumber,
		freeText = this.freeText,
		gasNumber = this.gasNumber,
		email = this.email,
		vatRegistered = this.vatRegistered,
		automaticPaymentLimit = this.automaticPaymentLimit,
		customPartsMarkup = this.customPartsMarkup,
		rewardPointValue = this.rewardPointValue,
		vatRate = this.vatRate,
		maxFee = this.maxFee,
		creditAmountMatrix = this.creditAmountMatrix,
		dealerPriceMatrix = this.dealerPriceMatrix,
		creditAmountAirToWaterHp = this.creditAmountAirToWaterHp,
		creditAmountAirToAirHp = this.creditAmountAirToAirHp,
		creditAmountWaterToWaterHp = this.creditAmountWaterToWaterHp,
		creditAmountBrineToWaterHp = this.creditAmountBrineToWaterHp,
		creditAmountFlowsave = this.creditAmountFlowsave,
		creditValueAirToWaterHp = this.creditValueAirToWaterHp,
		creditValueAirToAirHp = this.creditValueAirToAirHp,
		creditValueWaterToWaterHp = this.creditValueWaterToWaterHp,
		creditValueBrineToWaterHp = this.creditValueBrineToWaterHp,
		creditValueFlowsave = this.creditValueFlowsave,
		creditValueCap = this.creditValueCap,
		validated = this.validated
	}): BrandForm {
		return new BrandForm(this.formId, brandName, labourCost, domain, phoneNumber, logoId, address,
			vatRegistrationNumber, freeText, gasNumber, email, vatRegistered, automaticPaymentLimit, customPartsMarkup,
			rewardPointValue, vatRate, maxFee, creditAmountMatrix,
			creditAmountAirToWaterHp, creditAmountAirToAirHp, creditAmountWaterToWaterHp, creditAmountBrineToWaterHp,
			creditAmountFlowsave, creditValueAirToWaterHp, creditValueAirToAirHp, creditValueWaterToWaterHp,
			creditValueBrineToWaterHp, creditValueFlowsave, creditValueCap, dealerPriceMatrix, validated);
	}

	fields(): Array<FormField<any>> {
		return [this.brandName, this.email, this.domain];
	}
}
